import { useDirectoryGetRecord, useDirectoryGetSchemas } from 'api/requests';
import { useCountries } from 'api/requests/country';
import { useTranslation } from 'libs/i18n';
import { getDirectoryFieldValue } from 'modules/directory/common/helpers';
import { useDirectoryRecordArchive } from 'modules/directory/common/hooks';

import { useFieldsConfig } from '../../common/hooks/useFieldsConfig';
import { useValidationSchema } from '../../common/hooks/useValidationSchema';
import { getInitialValues } from '../helpers';

export const useDirectoryRecord = (schemaId: string, recordId: string) => {
  const { t, language } = useTranslation();

  const {
    record: currentRecord,
    loading: loadingRecord,
    error,
  } = useDirectoryGetRecord(recordId);

  const { schemas: allSchemas, loading: loadingSchemas } =
    useDirectoryGetSchemas();

  const { countries, loading: loadingCountries } = useCountries();

  const currentSchema = allSchemas?.find((schema) => schema.name === schemaId);

  const fieldsConfig = useFieldsConfig({
    currentSchema,
    allSchemas,
  });

  if (currentRecord?.fields && currentSchema && allSchemas) {
    currentRecord.fields = currentRecord.fields.map((fieldValue) => ({
      ...fieldValue,
      value: getDirectoryFieldValue(fieldValue, currentSchema, allSchemas),
    }));
  }

  const initialValues = getInitialValues({
    t,
    language,
    currentSchema,
    currentRecord,
    allSchemas,
    countries,
  });

  const validationSchema = useValidationSchema(currentSchema);

  // archive
  const { onArchiveRecord, onDearchiveRecord } = useDirectoryRecordArchive();

  const onArchive =
    !currentRecord?.isArchived && currentRecord?.isArchivable && onArchiveRecord
      ? async () => onArchiveRecord(currentRecord!)
      : undefined;

  const onDearchive =
    currentRecord?.isArchived && onDearchiveRecord
      ? async () => onDearchiveRecord(currentRecord!)
      : undefined;

  return {
    directoryName: currentSchema?.label[language] || currentSchema?.label.ru,
    currentSchema,
    allSchemas,
    currentRecord,
    initialValues,
    validationSchema,
    fieldsConfig,
    loading:
      (!currentRecord && loadingRecord) ||
      (!allSchemas && loadingSchemas) ||
      (!countries && loadingCountries) ||
      !initialValues,
    notFound: !!error || (!loadingRecord && !currentRecord),
    onArchive,
    onDearchive,
  };
};
